import * as React from "react"
import { cn } from "@/lib/utils"

export interface SwitchProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label?: string;
  checked?: boolean;
  defaultChecked?: boolean;
  onValueChange?: (checked: boolean) => void;
}

export const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  ({ className, checked, defaultChecked, disabled, label, onValueChange, ...props }, ref) => {
    const [isChecked, setIsChecked] = React.useState(defaultChecked ?? false);
    const id = props.id || React.useId();

    React.useEffect(() => {
      if (checked !== undefined) {
        setIsChecked(checked);
      }
    }, [checked]);

    const handleClick = () => {
      if (!disabled) {
        const newValue = !isChecked;
        setIsChecked(newValue);
        onValueChange?.(newValue);
      }
    };

    return (
      <div className="flex items-center space-x-2">
        <button
          type="button"
          role="switch"
          aria-checked={isChecked}
          aria-labelledby={label ? `${id}-label` : undefined}
          className={cn(
            "peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center premium-radius-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
            className
          )}
          ref={ref}
          disabled={disabled}
          onClick={handleClick}
          {...props}
        >
          <span
            className={cn(
              "pointer-events-none block h-5 w-5 premium-radius-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0",
              isChecked ? "translate-x-5" : "translate-x-0"
            )}
          />
        </button>
        {label && (
          <label
            id={`${id}-label`}
            htmlFor={id}
            className={cn(
              "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
              disabled && "cursor-not-allowed opacity-70"
            )}
          >
            {label}
          </label>
        )}
      </div>
    );
  }
);

Switch.displayName = "Switch" 
